// src/components/Supplier/InterviewProfilesDetails.js
import React, { useState, useEffect } from "react";
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Pagination,
    Stack,
    Paper,
    TextField,
    Chip,
    Link,
    Tooltip
} from "@mui/material";
import ScaleLoader from "react-spinners/ScaleLoader";
import noTableData from "../../assets/images/noTableData.svg";
import API from "../../utils/Axios.utils";
import useStyles from "../../assets/styles/SupMangComp.style";
import useDebounce from "../../utils/useDebounce";
import useAuth from "../../hooks/useAuth";
import { parseStatusLabel } from "../../utils/statusUtils";

const InterviewProfilesDetails = ({ handleDialog }) => {
    const classes = useStyles();
    const [showLoader, setShowLoader] = useState(false);

    const [searchText, setSearchText] = useState("");
    const debouncedSearchText = useDebounce(searchText, 500);
    const [profiles, setProfiles] = useState([]);
    const [listCount, setListCount] = useState(0);
    const [page, setPage] = useState(1);

    const handlePageChange = (e, value) => {
        setPage(value);
    };

    const { token, userId: supplierId } = useAuth();
    const config = {
        headers: { Authorization: `Bearer ${token}` },
    };

    const getInterviewProfiles = async () => {
        setShowLoader(true);
        let url = `/api/dash/interview-profiles-deatils/${supplierId}?page=${page}&limit=10`;
        if (debouncedSearchText.trim()) {
            url += `&search=${encodeURIComponent(debouncedSearchText.trim())}`;
        }

        try {
            const res = await API.get(url, config);
            setProfiles(res?.data?.candidates || []);
            setListCount(res?.data?.total_count || 0);
        } catch (error) {
            console.log("error", error);
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        getInterviewProfiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearchText]);


    return (
        <div>
            <Grid container xs={12}>
                <Grid
                    container
                    xs={12}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item>
                        <span className={classes.headingText}>Interview Profiles</span>
                    </Grid>
                    <Grid item>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search by Candidate"
                            value={searchText}
                            onChange={(e) => {
                                setPage(1);
                                setSearchText(e.target.value);
                            }}
                            style={{ minWidth: 260 }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid container xs={12} className={classes.tableGrid}>
                {showLoader ? (
                    <div
                        style={{
                            display: "flex",
                            height: 400,
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
                    </div>
                ) : profiles.length === 0 ? (
                    <div
                        style={{
                            height: 300,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                        }}
                    >
                        <img src={noTableData} width={60} alt="No Data" />
                        <div
                            style={{
                                marginTop: 5,
                                fontWeight: "500",
                                fontSize: 25,
                                fontFamily: "'Noto Sans', sans-serif",
                            }}
                        >
                            There are no interview profiles to show.
                        </div>
                    </div>
                ) : (
                    <TableContainer component={Paper}>
                        <Table
                            sx={{ minWidth: 650 }}
                            size="small"
                            aria-label="interview table"
                        >
                            <TableHead>
                                <TableRow className={classes.tableHeadRow}>
                                    <TableCell className={classes.tableHeadCell}>
                                        Candidate
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Status
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Job Code
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Job Title
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Location
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Scheduled Date
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Interview Link
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody className={classes.noHorizontalBorderTableRow}>
                                {profiles.map((row) => (
                                    <TableRow key={row.profile_id}>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.candidate_name}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            <Tooltip
                                                title={parseStatusLabel(row?.detailed_status) || 'No detailed status'}
                                                arrow
                                                placement="top"
                                            >
                                                <Chip
                                                    // label={row?.detailed_status}
                                                    label={parseStatusLabel(row?.detailed_status)}
                                                    color={
                                                        row.status_group === "Interview_Scheduled"
                                                            ? "success"
                                                            : "error"
                                                    }
                                                    size="small"
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.jobDetails?.job_code || row.job_code || "-"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.jobDetails?.title || "-"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.candidate_current_location}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row?.interview_datetime}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {/* {row?.interview_link} */}
                                            {row?.interview_link ? (
                                                <Link
                                                    href={row.interview_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="hover"
                                                    color="primary"
                                                    sx={{
                                                        wordBreak: "break-word",
                                                        maxWidth: 200,
                                                        display: "block",
                                                    }}
                                                >
                                                    {row.interview_link.length > 40
                                                        ? `${row.interview_link.slice(0, 40)}...`
                                                        : row.interview_link}
                                                </Link>
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Stack
                            spacing={2}
                            sx={{
                                padding: 2,
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "end",
                            }}
                        >
                            <Pagination
                                classes={{ root: classes.pagination }}
                                count={Math.ceil(listCount / 10)}
                                page={page}
                                onChange={handlePageChange}
                                showFirstButton
                                showLastButton
                            />
                        </Stack>
                    </TableContainer>
                )}
            </Grid>
        </div>
    );
};

export default InterviewProfilesDetails;
